import React from 'react';
import { WebsiteHeaderLayout_VideoArch } from '@apps/guest/websiteHeader';
import { EventNameNav, EventNameNavProps } from '../components/EventNameNav';
import { HeaderPhoto } from '../components/HeaderPhoto/HeaderPhoto';
import { VideoArch } from '@apps/guest/packages/layout-engine/widgets/Custom/components/VideoArch/VideoArch';
import { useColorPaletteProvider } from '../../../../ColorPaletteProvider/ColorPaletteProvider';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { InlineEditor } from '@shared/components';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { useTranslation } from '@shared/core';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { VideoArchProps } from '@apps/guest/packages/layout-engine/widgets/Custom/components/VideoArch/VideoArch.types';
import { Box } from '@withjoy/joykit';
import { isInIframe } from '@shared/utils/isInIframe';

interface VideoArchLayoutProps extends EventNameNavProps {
  websiteHeader: WebsiteHeaderLayout_VideoArch;
  showPlaceholder?: boolean;
}

const isPreview = isInIframe();

export const VideoArchLayout = (props: VideoArchLayoutProps) => {
  const { websiteHeader, ...restProps } = props;

  const photo = websiteHeader.headerPhoto.photos?.[0];
  const { video } = websiteHeader.headerPhoto;

  const { pageColor, pageFontColor } = useColorPaletteProvider();

  const showPlaceholder = isPreview && !!websiteHeader.headerPhoto.presentationLayout;

  const isMobileScreen = useIsMobileScreen();
  const videoUrl = isMobileScreen && video?.mobileUrl ? video.mobileUrl : video?.desktopUrl;

  // Determine if compact view (similar to VideoBackgroundLayout logic)
  // For videoArch, we typically don't use compact view, but keeping the structure consistent
  const isCompactView = false;

  const { tq } = useTranslation('websiteHeader');

  const handleEditPhotoClick = useEventCallback(() => {
    // send the message to the parent window to open the photo editor
    sendMessageToParentWindow({
      action: 'inlineEditingInteraction',
      source: 'joyPreview',
      value: { action: 'editHeaderPhoto' },
    });
  });

  const renderWrapper = useEventCallback((content: React.ReactNode, _context: { photoProps: VideoArchProps['photoProps']; videoProps: VideoArchProps['videoProps'] }) => {
    return (
      <Box
        position="absolute"
        top={0}
        left={0}
        width="100%"
        height="100%"
        display="flex"
        alignItems="center"
        justifyContent="center"
        zIndex="docked"
      >
        <InlineEditor
          elementLabel={tq`Photo`}
          wrapperType="actionInside"
          actionData={{
            editPhoto: handleEditPhotoClick,
          }}
          componentName="headerPhoto"
          pageName={restProps.page}
          pageSlug={restProps.pageSlug || ''}
          isEligibleForInlineEditing={true}
          wrapperCSS={{
            width: '100%',
          }}
        >
          {content}
        </InlineEditor>
      </Box>
    );
  });

  return (
    <EventNameNav
      eventDisplayName={websiteHeader.eventDisplayName}
      {...restProps}
    >
      <HeaderPhoto
        websiteHeader={websiteHeader}
        pageSlug={restProps.pageSlug}
        pageType={restProps.page}
        showPlaceholder={false}
      >
        <VideoArch
          photoProps={{
            photo: photo
              ? {
                  ...photo,
                }
              : undefined,
            innerArchColor: pageColor,
            outerArchColor: pageFontColor,
            showPlaceholder: false,
          }}
          videoProps={{
            videoUrl,
            thumbnailUrl: video?.placeholderImageUrl,
            showPlaceholder,
            height: '100vh',
            isCompactView,
            isMobile: isMobileScreen && !!video?.mobileUrl,
          }}
          renderWrapper={renderWrapper}
        />
      </HeaderPhoto>
    </EventNameNav>
  );
};

VideoArchLayout.displayName = 'VideoArchLayout';
